import { Button } from '@mantine/core';
import { ReactElement } from 'react';

import NullComponent from '@/components/NullComponent.tsx';
import PatientRecordLink from '@/components/PatientRecordLink.tsx';

import useConfig from '@/hooks/useConfig.ts';

import { CardReaderStep } from '@/models/CardReader.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { CardReadActionType } from '@/models/doc-cirrus/DCCardReader.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { WalkInTab, useWalkInStore } from '@/stores/walkInStore.ts';

import { applyCardReadAction, startBooking } from '@/utils/cardReadUtils.ts';
import { triggerFormSubmit } from '@/utils/formUtils.ts';
import { openPatientModal } from '@/utils/modalUtils.tsx';
import { convertUserToGenericPatient } from '@/utils/patientUtils.ts';

interface Props {
    onConfirm: () => void;
    isCardReadModal?: boolean;
}

const WalkInModalFooter = ({ onConfirm, isCardReadModal = false }: Props) => {
    const { customer_id } = useConfig();
    const { active_tab, is_user_selected, is_loading, card_read_state, selected_instance_id } = useWalkInStore();

    const getSuccessButtonText = (): string => {
        if (selected_instance_id) {
            return 'Patient:in zum Termin hinzufügen';
        }

        return isCardReadModal ? 'Patientenakte öffnen' : 'Termin buchen';
    };

    const buildCardReadCtas = (): ReactElement => {
        const handleSuccessClick = (): void => {
            const patient = card_read_state?.patients[0];
            if (!patient) {
                return;
            }

            if (selected_instance_id) {
                const generic_patient = convertUserToGenericPatient(patient, PVSProvider.DOC_CIRRUS);
                useBookingStore.setState({ current_patient: generic_patient });
                onConfirm();
                return;
            }

            if (isCardReadModal) {
                openPatientModal(
                    patient.user_id,
                    patient.eterno_user_id,
                    patient.instance_id,
                    `${patient.name.last_name} ${patient.name.first_name}`
                );
            } else {
                startBooking(patient);
            }
        };

        const getPatientFolderLinkElement = (): ReactElement | undefined => {
            const patient = card_read_state?.patients[0];
            if (!patient) {
                return undefined;
            }

            const generic_patient = convertUserToGenericPatient(patient, PVSProvider.DOC_CIRRUS);
            if (!generic_patient?.pvs_user_id || !generic_patient?.instance_id) return undefined;

            return (
                <PatientRecordLink
                    pvsPatientId={generic_patient.pvs_user_id}
                    instanceId={generic_patient.instance_id}
                />
            );
        };

        switch (card_read_state?.step) {
            case CardReaderStep.SUCCESS: {
                return (
                    <div className="flex w-full flex-row justify-end gap-8">
                        {getPatientFolderLinkElement()}
                        <Button disabled={is_loading} onClick={handleSuccessClick}>
                            {getSuccessButtonText()}
                        </Button>
                    </div>
                );
            }
            case CardReaderStep.MERGED: {
                return (
                    <div className="flex w-full flex-row justify-between">
                        {card_read_state.possible_actions.includes(CardReadActionType.CANCEL) && (
                            <Button
                                disabled={is_loading}
                                variant="outline"
                                onClick={() =>
                                    applyCardReadAction(
                                        card_read_state,
                                        card_read_state.patients[0],
                                        CardReadActionType.CANCEL,
                                        customer_id
                                    )
                                }
                            >
                                Vorgang abbrechen
                            </Button>
                        )}
                        {card_read_state.possible_actions.includes(CardReadActionType.APPLY) && (
                            <Button
                                disabled={is_loading}
                                onClick={() =>
                                    applyCardReadAction(
                                        card_read_state,
                                        card_read_state.patients[0],
                                        CardReadActionType.APPLY,
                                        customer_id
                                    )
                                }
                            >
                                Patientenakte aktualisieren
                            </Button>
                        )}
                    </div>
                );
            }
            case CardReaderStep.MATCHING: {
                return card_read_state?.patients[0] &&
                    card_read_state.possible_actions.includes(CardReadActionType.CANCEL) ? (
                    <div className="flex w-full flex-row justify-between">
                        <Button
                            disabled={is_loading}
                            variant="outline"
                            onClick={() =>
                                applyCardReadAction(
                                    card_read_state,
                                    card_read_state.patients[0],
                                    CardReadActionType.CANCEL,
                                    customer_id
                                )
                            }
                        >
                            Vorgang abbrechen
                        </Button>
                    </div>
                ) : (
                    <NullComponent />
                );
            }
            case CardReaderStep.SELECT_READER:
            case CardReaderStep.RETRY:
            case CardReaderStep.CANCELLED: {
                return (
                    <div className="flex w-full flex-row justify-between">
                        <Button
                            disabled={is_loading}
                            onClick={() =>
                                useWalkInStore.setState({
                                    card_read_state: {
                                        ...card_read_state,
                                        step: CardReaderStep.SELECT_READER,
                                    },
                                })
                            }
                        >
                            {card_read_state.step === CardReaderStep.CANCELLED
                                ? 'Neu starten'
                                : 'Noch einmal versuchen'}
                        </Button>
                    </div>
                );
            }
            default: {
                return <NullComponent />;
            }
        }
    };

    const card_read_ctas: ReactElement = buildCardReadCtas();

    switch (active_tab) {
        case WalkInTab.SEARCH: {
            return (
                <div className="flex flex-row gap-4" key={0}>
                    <Button
                        disabled={!is_user_selected}
                        // variant={is_card_read_available ? 'outline' : 'filled'}
                        variant="filled"
                        onClick={onConfirm}
                    >
                        {selected_instance_id ? 'Patient zum Termin hinzufügen' : 'Termin buchen'}
                    </Button>
                    {/*<ComingSoon key={1}>*/}
                    {/*    <Button disabled variant="filled" onClick={onConfirm}>*/}
                    {/*        Weiter mit eGK einlesen*/}
                    {/*    </Button>*/}
                    {/*</ComingSoon>*/}
                </div>
            );
        }
        case WalkInTab.READ_CARD: {
            return card_read_ctas;
        }
        case WalkInTab.NEW_PATIENT: {
            return (
                <Button className="w-52" onClick={triggerFormSubmit} loading={is_loading}>
                    Patient:in anlegen
                </Button>
            );
        }
        default: {
            return <NullComponent />;
        }
    }
};

export default WalkInModalFooter;
